import { useSyncExternalStore } from "react";
import {
  TENANT_ACCESS_DENIAL_CODES,
  type TenantAccessDenialCode,
} from "../../../shared/types/accessScope";

/**
 * The server's answers that mean "this user can't use this instance at all"
 * (deny-by-default, Phase U), as opposed to one refused action. NOT_AUTHENTICATED
 * is a 401: signing in again handles it, so it never reaches this screen.
 */
export type AccessDenialCode = Exclude<TenantAccessDenialCode, "NOT_AUTHENTICATED">;

export interface AccessDenial {
  code: AccessDenialCode;
  /** The instance refused, when known: tried again, and left out of Switch instance. */
  instanceId?: string | null;
}

const DENIAL_CODES: ReadonlySet<string> = new Set(
  TENANT_ACCESS_DENIAL_CODES.filter((code) => code !== "NOT_AUTHENTICATED"),
);

/** The denial a response body carries (`{ error, code }`), or null for any other answer. */
export function denialCodeOf(body: unknown): AccessDenialCode | null {
  const code = (body as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" && DENIAL_CODES.has(code) ? (code as AccessDenialCode) : null;
}

/**
 * What POST /auth/resolve-instance's 200 says: null with access, else its `code`.
 * An older server sends no code; then an instance that exists means NO_ASSIGNMENT
 * and one that doesn't means INSTANCE_NOT_FOUND.
 */
export function denialFromResolve(data: {
  hasAccess?: boolean;
  code?: unknown;
  instance?: { status?: string } | null;
}): AccessDenialCode | null {
  if (data.hasAccess === true) return null;
  return (
    denialCodeOf(data) ??
    (data.instance?.status === "not_found" ? "INSTANCE_NOT_FOUND" : "NO_ASSIGNMENT")
  );
}

// One denial per page load. The first one wins: whatever is refused after it
// (the app's other requests finishing) can't change the screen under the user.
// Leaving the screen is a full page load (Switch instance, Try again, Sign out).
let current: AccessDenial | null = null;
const listeners = new Set<() => void>();

export function reportAccessDenial(denial: AccessDenial): void {
  if (current) return;
  current = denial;
  listeners.forEach((listener) => listener());
}

export function getAccessDenial(): AccessDenial | null {
  return current;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The denial reported so far, re-rendering when one arrives. */
export function useAccessDenial(): AccessDenial | null {
  return useSyncExternalStore(subscribe, getAccessDenial, getAccessDenial);
}

export function resetAccessDenialForTests(): void {
  current = null;
  listeners.forEach((listener) => listener());
}
